import { chunk, join, reverse, snakeCase } from 'lodash'
import { NetworkStatus } from '@apollo/client'

import { TInputMask } from '@/components/Form/types'
import {
  ICar,
  Maybe,
  FetchMoreStatus,
  IDriver,
  ICustomer,
  OrderType,
  DataType,
} from '@/types'
import { t } from '@/i18n'
import { AuthUserNode, MeNode } from '@/apollo/graphql'

export const formatNumber = (value: string | number, delimiter = ',') => {
  if (isNaN(+value)) return value
  const intPart = `${value}`.split('.')[0]
  const decimalPart = `${value}`.split('.')[1]
  const newIntPart = join(
    reverse(
      chunk(reverse(intPart.split('')), 3).map(s => join(reverse(s), '')),
    ),
    delimiter,
  )

  const newNumberStr = isNaN(+decimalPart)
    ? newIntPart
    : `${newIntPart}.${decimalPart}`

  return newNumberStr
}

export function cleanList<T>(list: Maybe<Maybe<T>[]>): T[] {
  if (!list) return []

  return list.filter(el => !!el) as T[]
}

export const transformData = (d: any) => {
  const _d: any = {}
  Object.entries(d).forEach(([key, value]) => {
    if (value === '') {
      return
    }
    _d[snakeCase(key)] = value
  })

  return _d
}

export const carDisplayLabel = (d: Partial<ICar>, isRTL: boolean): string => {
  const model = isRTL
    ? d.carSubModel?.carModel.localName
    : d.carSubModel?.carModel.name
  const subModel = isRTL ? d.carSubModel?.localName : d.carSubModel?.name
  let label = ''
  if (d.number) {
    label = `${getMaskedString(d.number, true)}`
  } else {
    if (model) {
      label = subModel ? `${model} (${subModel})` : model
    } else {
      label = subModel ? subModel : ''
    }
  }
  return label
}

export const carModelDisplayLabel = (
  d: Partial<ICar>,
  isRTL: boolean,
): string => {
  return (
    (isRTL
      ? d.carSubModel?.carModel.localName
      : d.carSubModel?.carModel.name) || ''
  )
}

export const carSubModelDisplayLabel = (
  d: Partial<ICar>,
  isRTL: boolean,
): string => {
  return (isRTL ? d.carSubModel?.localName : d.carSubModel?.name) || ''
}

export const driverDisplayLabel = (
  driver?: Maybe<Partial<IDriver>>,
): string => {
  if (!driver) return ''

  let label = ''
  if (driver?.name) {
    label = driver?.name
  } else if (driver?.email) {
    label = driver?.email
  } else if (driver?.idNumber) {
    label = driver?.idNumber
  } else if (driver?.id) {
    label = `${t('driver')} - ${driver?.id}`
  }

  return label
}

export const customerDisplayLabel = (
  customer: Maybe<Partial<ICustomer>>,
): string => {
  if (!customer) return ''
  return (
    `${customer.name || customer.email} - ${customer.id}` || `${customer.id}`
  )
}

export const userDisplayLabel = (
  user: Maybe<Partial<AuthUserNode | MeNode>>,
): string => {
  if (!user) return ''
  // @ts-ignore
  if (user.fullName) return user.fullName
  const userName = [user.firstName, user.lastName]
    .filter(d => !!d)
    .join(' ')
    .trim()

  return userName || user.email || ''
}

export const getFetchMoreStatus = ({
  networkStatus,
  hasNextPage,
  withForce = false,
}: {
  networkStatus: NetworkStatus
  hasNextPage?: boolean
  withForce?: boolean
}): FetchMoreStatus => {
  if (networkStatus === NetworkStatus.loading) return 'loading'
  if (hasNextPage) return 'hasNextPage'

  return withForce ? 'end-with-force' : 'end'
}

/**
 * Get string mask-applied
 * @param value
 * @param mask
 * @returns string
 */
export function getMaskedString(
  value: Maybe<string>,
  isCarNumber?: boolean,
  mask?: TInputMask,
): string {
  if (!value || (!isCarNumber && !mask)) return value || ''

  let res = value
  let matchRegEx = /^$/ // RegExp for grouping, needs to be set for each Mask
  let delimiter: string[] = [] // Delimiter symbol to concat groups, needs to be set for each Mask

  let _mask: TInputMask | undefined = mask
  if (isCarNumber) {
    _mask = value.length < 8 ? '00-000-00' : '000-00-000'
  }
  if (_mask === '00-000-00') {
    res = res.replace(/\D/g, '') // Remove all non-digit characters
    matchRegEx = /(\d{0,2})(\d{0,3})(\d{0,2})/
    delimiter = ['-', '-']
  } else if (_mask === '000-00-000') {
    res = res.replace(/\D/g, '') // Remove all non-digit characters
    matchRegEx = /(\d{0,3})(\d{0,2})(\d{0,3})/
    delimiter = ['-', '-']
  }

  const group = res.match(matchRegEx) || []

  if (!group[2]) return group[1] || ''

  res = group[1] || ''
  group.forEach((item, index) => {
    if (index > 1) {
      res += item ? `${delimiter[index - 2] || ''}${item}` : ''
    }
  })

  return res
}

export const sort = (
  a: any,
  b: any,
  sortKey?: string,
  orderBy?: OrderType,
  dataType?: DataType,
) => {
  if (sortKey) {
    const valA = a[sortKey]
    const valB = b[sortKey]

    if (
      (sortKey || '').match(/time|date|Time|Date/g)?.length ||
      dataType === 'date'
    ) {
      if (orderBy === 'desc') {
        return new Date(valB).getTime() - new Date(valA).getTime()
      }
      return new Date(valA).getTime() - new Date(valB).getTime()
    }

    if (typeof valA === 'string' && typeof valB === 'string') {
      if (orderBy === 'desc') {
        return valB.localeCompare(valA)
      }
      return valA.localeCompare(valB)
    }

    if (orderBy === 'desc') {
      return valB - valA
    }
    return valA - valB
  }
  return 0
}
